import PageHeader from "../../../components/Page/PageHeader";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, MouseEvent } from "react";
import SnackBar from "../../../components/Alerts/SnackBar";
import useMessage from "../../../hooks/useMessage";
import { useGetReadacrossCountReadacrossCountGet, useGetReadacrossReadacrossGet, useGetReadacrossTagsReadacrossTagsGet } from "../../../api/generated/fastAPI";
import SearchBar from "../../../components/Filtering/SearchBar";
import ColumnsButton from "../../../components/Buttons/ColumnsButton";
import { GetReadacrossReadacrossGetParams, ReadAcross } from "../../../api/models";
import GenerateReadAcrossModal from "../components/GenerateReadAcrossModal";
import { TableData, TableHeader } from "../../../components/Table/DataTable";
import { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import ErrorScreen from "../../../components/Alerts/ErrorScreen";
import FilterBar, { FilterBarType } from "../../../components/Filtering/FilterBar";
import DeleteModal, { DeleteItemType } from "../../../components/Popups/DeleteModal";
import useDataParams from "../../../hooks/useDataParams";
import UpdatedPagination from "../../../components/Filtering/UpdatedPagination";
import useDelete from "../../../hooks/endpoints/useDelete";
import VirtualizedDataTable from "../../../components/Table/VirtualizedDataTable";
import useDownload from "../../../hooks/endpoints/useDownload";
import NestedDropdown from "../../../components/Menus&Dropdowns/NestedDropdown";

const raHeaders: TableHeader[] = [
    {
        label: 'Name',
        value: 'name',
        isShown: true,
        alignment: 'left',
    },
    {
        label: 'Compound',
        value: 'smiles',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Dataset',
        value: 'readacross_type',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Tags',
        value: 'tags',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Created By',
        value: 'created_by',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Owned By',
        value: 'owner',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Created',
        value: 'creation_date',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Updated',
        value: 'updated_date',
        isShown: true,
        alignment: 'center',
    },
];

const defaultParams: GetReadacrossReadacrossGetParams = {
    name: null,
    created_by: null,
    owner: null,
    start_date: null,
    end_date: null,
    tag_groups: null,
    readacross_type: null,
    smiles: null,
    limit: 10,
    offset: 0,
    sort_by: 'creation_date',
    sort_order: 'desc',
}

const defaultFilters: FilterBarType[] = [
    {
        label: 'Created Date Range',
        param: 'start_date',
        type: 'date-range',
        isDefault: true,
        groupKey: 'date-range',
    },
    {
        label: 'Created Date Range',
        param: 'end_date',
        type: 'date-range',
        isDefault: true,
        groupKey: 'date-range',
    },
    {
        label: 'Tags',
        param: 'tag_groups',
        type: 'tags',
        isDefault: true,
    },
    {
        label: 'Created By',
        param: 'created_by',
        type: 'users',
        isDefault: true,
    },
    {
        label: 'Owner',
        param: 'owner',
        type: 'users',
        isDefault: true,
    },
]


export default function ReadAcrossPage() {
    const getRATagsMutation = useGetReadacrossTagsReadacrossTagsGet();

    const {
        params,
        setParams,
        search,
        sort,
        filters,
        setPaginationLimit,
        setPaginationOffset
    } = useDataParams({
        defaultParams,
        defaultFilters,
        tagsData: getRATagsMutation.data as unknown as { [key: string]: string[] },
    })

    const getReadAcrossMutation = useGetReadacrossReadacrossGet(params as GetReadacrossReadacrossGetParams);
    const getReadAcrossCountMutation = useGetReadacrossCountReadacrossCountGet({
        name: (params as GetReadacrossReadacrossGetParams).name!,
        created_by: (params as GetReadacrossReadacrossGetParams).created_by!,
        owner: (params as GetReadacrossReadacrossGetParams).owner!,
        start_date: (params as GetReadacrossReadacrossGetParams).start_date!,
        end_date: (params as GetReadacrossReadacrossGetParams).end_date!,
        tag_groups: (params as GetReadacrossReadacrossGetParams).tag_groups!,
    });
    const [ count, setCount ] = useState(0);

    const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();

    const navigate = useNavigate();
    const [ selectedItems, setSelectedItems ] = useState<(string | number)[]>([]);
    const [ tableHeaders, setTableHeaders ] = useState<TableHeader[]>(raHeaders);
    const [ ras, setRas ] = useState<TableData[]>([]);
    const [ allRas, setAllRas ] = useState<ReadAcross[]>([]);
    const [ actionDropdownOptions, setActionDropdownOptions ] = useState<DropdownOption[]>([]);

    const refetchRA = () => {
        getReadAcrossMutation.refetch();
        getReadAcrossCountMutation.refetch();
    }

    const { deleteRAs } = useDelete({ setPopupMessage, refetchFn: refetchRA });
    const { downloadReadacrossData } = useDownload();

    useEffect(() => {
        if (getReadAcrossMutation.data) {
            const readacrossFiles = getReadAcrossMutation.data as unknown as ReadAcross[];
            setAllRas(readacrossFiles);

            if (readacrossFiles.length > 0) {
                const items: TableData[] = readacrossFiles.map(file => ({
                    id: file.id!,
                    fields: [
                        {
                            associatedColumn: 'name',
                            value: file.name ? file.name : 'No value',
                            alignment: 'left' as any,
                            type: 'text' as any,
                            action: (e:MouseEvent<HTMLButtonElement>) => navigate(`/readacross/${file.id!}`),
                        },
                        {
                            associatedColumn: 'smiles',
                            value: file.smiles!,
                            alignment: 'center',
                            type: 'structure',
                        },
                        {
                            associatedColumn: 'readacross_type',
                            value: file.readacross_type ? file.readacross_type : 'No value',
                            alignment: 'center',
                            type: 'text',
                        },
                        {
                            associatedColumn: 'tags',
                            value: file.readacross_metadata!.tags! as string[],
                            alignment: 'center' as any,
                            type: 'tags' as any,
                        },
                        {
                            associatedColumn: 'created_by',
                            value: file.created_by ? file.created_by : 'No value',
                            alignment: 'center',
                            type: 'text' as any,
                        },
                        {
                            associatedColumn: 'owner',
                            value: file.owner ? file.owner : "No value",
                            alignment: 'center' as any,
                            type: 'text' as any,
                        },
                        {
                            associatedColumn: 'creation_date',
                            value: file.creation_date ? new Date(file.creation_date!).toLocaleString('en-US', {timeZone: 'EST'}) : 'No value',
                            alignment: 'center' as any,
                            type: 'text' as any,
                        },
                        {
                            associatedColumn: 'updated_date',
                            value: file.updated_date ? new Date(file.updated_date!).toLocaleString('en-US', {timeZone: 'EST'}) : 'No value',
                            alignment: 'center' as any,
                            type: 'text' as any,
                        },
                    ],
                    options: injectOptions(file),
                }));

                setRas(items);
            }
        }

        //eslint-disable-next-line
    }, [getReadAcrossMutation.data]);

    useEffect(() => {
        if (getReadAcrossCountMutation.data) {
            const data = getReadAcrossCountMutation.data as unknown as number;
            setCount(data);
        }
    }, [getReadAcrossCountMutation.data]);

    useEffect(() => {
        const options: DropdownOption[] = [];

        if (allRas.length > 0 && selectedItems.length > 0) {
            let items: DeleteItemType[] = [];
            const exportItems: ReadAcross[] = [];
            selectedItems.forEach(id => {
                const matchingItem = allRas.find(ra => ra.id === id);
                if (matchingItem) {
                    items.push({
                        type: 'readacross',
                        id,
                        name: matchingItem.name ? matchingItem.name : ''
                    });

                    exportItems.push(matchingItem);
                }
            });

            options.push({
                label: 'Export',
                action: (e:MouseEvent<HTMLButtonElement>) => {},
                moreOptions: [
                    {
                        label: 'XLSX',
                        action: async (e:MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();

                            const promises = exportItems.map(async (item) => {
                                return downloadReadacrossData('.xlsx', item);
                            })

                            try {
                                await Promise.all(promises);
                                setSelectedItems([]);
                            } catch (err:any) {
                                console.log(err);
                            };
                        },
                    }
                ]
            });

            const deleteModalComp: DropdownOption = {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={items}
                        cancelFn={() => setSelectedItems([])}
                        deleteFn={deleteRAs}
                    />
                ),
            };

            options.push(deleteModalComp);
        };

        setActionDropdownOptions(options);

        //eslint-disable-next-line
    }, [selectedItems, allRas]);

    const injectOptions = (ra: ReadAcross) => {
        const id = ra.id!;
        const name = ra.name!;

        const options: DropdownOption[] = [
            {
                label: 'Export',
                action: (e:MouseEvent<HTMLButtonElement>) => {},
                moreOptions: [
                    {
                        label: 'XLSX',
                        action: async (e:MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();

                            try {
                                await downloadReadacrossData(
                                    '.xlsx',
                                    ra,
                                )
                            } catch (err:any) {
                                console.log(err);
                            }
                        },
                    }
                ]
            },
            {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={[{
                            type: 'readacross',
                            id,
                            name,
                        }]}
                        deleteFn={deleteRAs}
                    />
                ),
            },
        ];

        return options;
    }
    

    return (
        <div className="p-10 h-screen overflow-hidden flex flex-col justify-start items-stretch gap-4">
       

            <PageHeader label='Read-Across'>
                <GenerateReadAcrossModal setPopupMessage={setPopupMessage} location="header"/>
            </PageHeader>

            <div className="flex flex-col justify-start items-start gap-2">
                <div className="w-full flex justify-between items-center gap-4">
                    <div className="max-w-[600px] w-full">
                        <SearchBar
                            searchFn={search}
                            searchKey="name"
                        />
                    </div>
                    <div className="flex justify-end items-center gap-4">
                        <ColumnsButton 
                            headers={tableHeaders} 
                            setHeaders={setTableHeaders} 
                            isDisabled={false}
                        />
                        {selectedItems.length > 0 && (
                            <NestedDropdown 
                                dir="right"
                                btn={{
                                    label: `Actions (${selectedItems.length} selected)`,
                                    type: 'secondary',
                                    styling: 'solid',
                                    id: 'ds-multi-action-btn',
                                }}
                                options={actionDropdownOptions}
                            />
                        )}
                    </div>
                </div>
                <FilterBar 
                    filters={filters}
                    setParams={setParams}
                    params={params}
                />
            </div>

            {getReadAcrossMutation.error && ras.length === 0 ? (
                <ErrorScreen errCode={(getReadAcrossMutation.error as any).code}/>
            ) : (
                <>
                    <VirtualizedDataTable 
                        paramValues={Object.entries(params).filter(entry => ['name', 'start_date', 'end_date'].includes(entry[0]) && entry[1]).map(val => val[1])}
                        isLoading={getReadAcrossMutation.isLoading} 
                        headers={tableHeaders} 
                        tableData={ras}
                        selection={{
                            selectedItems,
                            setSelectedItems
                        }}
                        setPopupMessage={setPopupMessage}
                        sorting={{
                            sortableHeaders: ['name', 'created_by', 'owner', 'creation_date', 'updated_date', 'smiles', 'readacross_type'],
                            sortHeaders: sort,
                            sortByParam: params.sort_by!,
                            sortOrderParam: params.sort_order!,
                        }}
                        fixedItemHeight={194}
                        count={getReadAcrossCountMutation.data as unknown as number | undefined}
                        dataType="readacross"
                    />
                    <UpdatedPagination
                        count={count}
                        limit={params.limit!}
                        offset={params.offset!}
                        setPaginationLimit={setPaginationLimit}
                        setPaginationOffset={setPaginationOffset}
                        size="reg"
                        isLoading={getReadAcrossCountMutation.isLoading}
                    />
                </>
            )}

            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  


            

        </div>
    )
}